"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { TextAnimate } from "./TextAnimation";
import SectionBadge from "./SectionBadge";
import { Marquee } from "./ui/marquee";
import { motion } from "motion/react";
import type { ConferencesSectionData } from "@/types/cms";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface ConferencesSectionProps {
  data?: ConferencesSectionData;
}

const defaultConferences = [
  { id: 1, src: "/trusts/HumanX.png", alt: "Human X" },
  { id: 2, src: "/trusts/Finovate.png", alt: "Finovate" },
  { id: 3, src: "/trusts/GPT DAO.png", alt: "Gen AI Summit" },
  { id: 4, src: "/trusts/ITAD.png", alt: "ITAD" },
  { id: 5, src: "/trusts/SaaStr.png", alt: "SaaStr" },
  { id: 6, src: "/trusts/BlackHat.png", alt: "BlackHat" },
  { id: 7, src: "/trusts/Insuretech.png", alt: "Insuretech" },
  { id: 8, src: "/trusts/Money2020.png", alt: "Money 20/20" },
];

export default function ConferencesSection({ data }: ConferencesSectionProps) {
  const sectionRef = useSectionTracking("Conferences Section", "conferences");
  const [isDesktop, setIsDesktop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsDesktop(width >= 1024); // lg breakpoint
      setIsMobile(width < 768); // mobile breakpoint
    };

    // Check on mount
    checkScreenSize();

    // Add event listener
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const badgeText = data?.badgeText || "Conferences";
  const title = data?.title || "It was seen at";
  const subtitle = data?.subtitle || "";

  const conferences =
    data?.conferenceLogos?.map((logo) => ({
      id: logo.id,
      src: logo.logo?.url || "",
      alt: logo.altText || logo.logo?.alternativeText || "Conference logo",
    })) || defaultConferences;
  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="conferences"
      className="relative bg-black py-8 md:py-12 px-4 font-host-grotesk overflow-hidden"
    >
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#5b4fe8]/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center">
          {/* <SectionBadge text={badgeText} className="mb-12" /> */}

          {/* Title - Optimized for mobile */}
          {isMobile ? (
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-white font-host-grotesk text-xl md:text-xl lg:text-2xl text-center max-w-200 mb-6"
            >
              {title}
            </motion.h3>
          ) : (
            <TextAnimate
              animation="blurInUp"
              by="line"
              className="text-white font-host-grotesk text-xl md:text-xl lg:text-2xl text-center max-w-200 mb-6"
              as="h3"
              duration={0.2}
              once={true}
            >
              {title}
            </TextAnimate>
          )}

          {/* Subtitle - Optimized for mobile */}
          {subtitle &&
            subtitle.trim() !== "" &&
            (isMobile ? (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-white/70 mt-4 text-center text-sm md:text-base mb-3"
              >
                {subtitle}
              </motion.p>
            ) : (
              <TextAnimate
                animation="blurInUp"
                by="line"
                className="text-white/70 mt-4 text-center text-sm md:text-base mb-3"
                as="p"
                duration={0.2}
                once={true}
              >
                {subtitle}
              </TextAnimate>
            ))}
        </div>

        {/* Logos Marquee Style Grid */}
        <div className="relative">
          {/* Top border with gradient */}
          <div className="absolute top-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />

          {/* Conditional Rendering based on screen size */}
          {!mounted || !isDesktop ? (
            // Grid Layout for Mobile/Tablet
            <div className="pt-4 pb-2 flex justify-center">
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 justify-items-center">
                {conferences.map((conference) => (
                  <div key={conference.id} className="group relative">
                    <div className="relative w-16 h-12 md:w-20 md:h-14 flex items-center justify-center">
                      <Image
                        src={conference.src}
                        alt={conference.alt}
                        fill
                        className="object-contain grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300 ease-out filter group-hover:drop-shadow-[0_0_8px_rgba(212,168,75,0.3)]"
                        sizes="120px"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Marquee for Desktop
            <div className="pt-4 pb-2 relative">
              {/* Left fade gradient */}
              <div className="absolute left-0 top-0 bottom-0 w-24 md:w-32 bg-linear-to-r from-black to-transparent z-10 pointer-events-none" />

              {/* Right fade gradient */}
              <div className="absolute right-0 top-0 bottom-0 w-24 md:w-32 bg-linear-to-l from-black to-transparent z-10 pointer-events-none" />

              <Marquee pauseOnHover className="[--duration:40s]">
                {conferences.map((conference) => (
                  <div
                    key={conference.id}
                    className="group relative mx-4 md:mx-6"
                  >
                    <div className="relative w-16 h-12 md:w-20 md:h-14 flex items-center justify-center">
                      <Image
                        src={conference.src}
                        alt={conference.alt}
                        fill
                        className="object-contain grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300 ease-out filter group-hover:drop-shadow-[0_0_8px_rgba(212,168,75,0.3)]"
                        sizes="120px"
                      />
                    </div>
                  </div>
                ))}
              </Marquee>
            </div>
          )}

          {/* Bottom border with gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
        </div>
      </div>
    </section>
  );
}
